import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';

import { TpDataTable, TpDataTableColumn } from '../../../../data/interfaces';
import { convertColumnDataToDisplayString, getHighlightedText } from '../../helpers/misc';
import { showErrorMessage } from '../../../../components/message-boxes/ErrorMessage';

import styles from './DataTable.module.scss';
import { useActionDispatcher } from './useActionDispatcher';
import { getCellStyle } from './cellUtils';
import {
  TpColumnActionConfig,
  TpActionConfig,
  TpColumnConfig,
  ACTION_STYLE_TYPE,
  getActionConfigByType,
} from './interfaces';

/***
 * Determines the icon to be used for the action or null if no icon should be displayed,
 * either because of the type or because it is missing.
 *
 * @returns An icon definition tuple [prefix, iconName] or null if no icon can be found in the configuration.
 */
function getIconForAction(
  columnConfig: TpColumnActionConfig,
  actionTypeConfig: TpActionConfig,
): [IconPrefix, IconName] | null {
  const styleType = columnConfig.actionStyle ?? actionTypeConfig.defaultPresentationType;
  const iconDefinition = columnConfig.actionIcon ?? actionTypeConfig.presentation[styleType]?.icon;
  if (!iconDefinition) return null;

  if (typeof iconDefinition === 'string') {
    if (iconDefinition.includes(':')) {
      const [prefix, iconName] = iconDefinition.split(':');
      return [prefix as IconPrefix, iconName as IconName];
    }
    return ['fas' as IconPrefix, iconDefinition as IconName];
  }
  return iconDefinition as [IconPrefix, IconName];
}

/***
 * Determines the tooltip to be used for the action, which is based on the label provided in the column configuration or the default label for the action type.
 */
function getTooltipForAction(columnConfig: TpColumnActionConfig, actionTypeConfig: TpActionConfig) {
  const styleType = columnConfig.actionStyle ?? actionTypeConfig.defaultPresentationType;
  return columnConfig.actionLabel ?? actionTypeConfig.presentation[styleType]?.label ?? '';
}

export interface TpActionCellProps {
  columnConfig: TpColumnConfig;
  colInfo: TpDataTableColumn;
  dataTable: TpDataTable;
  rowIndex: number;
  searchString: string;
}

/**
 * A table cell component used to display an action in the data table.
 * The action to be performed is determined by the column configuration.
 */
export function ActionTableCell({ columnConfig, colInfo, dataTable, rowIndex, searchString }: TpActionCellProps) {
  // Imports/hooks
  const actionHandlers = useActionDispatcher();

  // In case of any misconfiguration, we return an empty cell and log a warning in the console.
  const defaultReturnValue = <td />;

  const actionConfig = columnConfig.action;
  if (!actionConfig) {
    console.warn(
      `No action config found for column ${colInfo.id}. Please provide an action config to use action column type.`,
    );
    return defaultReturnValue;
  }

  const propertyColumnId = actionConfig.requiredProperty ?? colInfo.id;
  const propertyColumn = dataTable.columnData[propertyColumnId];
  if (!propertyColumn) {
    console.warn(
      `No column data found for required property ${propertyColumnId} in action column ${colInfo.id}. Please verify the action configuration and dataset.`,
    );
    return defaultReturnValue;
  }
  const propertyValue = propertyColumn[rowIndex];

  // If the action requires a specific property value and the current cell value doesn't match, we don't render the action.
  if (actionConfig.requiredPropertyValues && !actionConfig.requiredPropertyValues.includes(propertyValue)) {
    return defaultReturnValue;
  }

  const actionTypeConfig = getActionConfigByType(actionConfig.type);
  if (!actionTypeConfig) {
    console.warn(
      `No action type config found for action type ${actionConfig.type} in column ${colInfo.id}. Please provide a valid action type in the column configuration.`,
    );
    return defaultReturnValue;
  }
  const styleType = actionConfig.actionStyle ?? actionTypeConfig.defaultPresentationType;
  const tooltip = getTooltipForAction(actionConfig, actionTypeConfig);
  const icon = getIconForAction(actionConfig, actionTypeConfig);

  const actionFunction = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    const handler = actionHandlers[actionConfig.type];
    if (handler) {
      handler(propertyValue);
    } else {
      showErrorMessage('No handler implemented for this row action. Please contact support.');
    }
  };

  if (styleType === ACTION_STYLE_TYPE.NAME) {
    const currentCellValue = dataTable.columnData[colInfo.id][rowIndex];
    const displayString = convertColumnDataToDisplayString(colInfo, currentCellValue);
    return (
      <td style={getCellStyle(columnConfig)} onClick={actionFunction}>
        <span className={styles.primaryLink} title={tooltip}>
          {getHighlightedText(displayString, searchString)}
          {icon && <FontAwesomeIcon icon={icon} className={styles.inlineOpenIcon} />}
        </span>
      </td>
    );
  }
  if (styleType === ACTION_STYLE_TYPE.ICON) {
    if (!icon) {
      console.warn(
        `No icon found for action type ${actionConfig.type} with style ${styleType} in column ${colInfo.id}.`,
      );
      return defaultReturnValue;
    }
    return (
      <td className={styles.actionCell} onClick={actionFunction}>
        <div className={styles.actionIconWrapper} title={tooltip}>
          <FontAwesomeIcon icon={icon} />
        </div>
      </td>
    );
  }

  console.warn(
    `Invalid style type ${styleType} for action type ${actionConfig.type}. Please provide a valid style type or an icon for the action.`,
  );
  return defaultReturnValue;
}
